import { useState } from "react";

interface FAQItem {
  question: string;
  answer: string;
}

const faqs: FAQItem[] = [
  {
    question: "How fast can I get a passport urgently?",
    answer: "With Fast Passport Center, urgent passport processing starts at 3 business days from when we receive your documents. Add 1–2 days for documents to reach us if mailing, plus overnight FedEx return delivery. Most customers in our 3-day tier have their passport in hand within 5–7 days of starting their order. For situations with less than 48 hours, call us immediately at 877-253-0084 — we'll assess what's possible in real time.",
  },
  {
    question: "Can I get an urgent passport without going to a passport agency?",
    answer: "For passport renewals (DS-82), yes — you never need to visit a passport agency or acceptance facility. Everything is done by mail. For new passports and lost/stolen replacements (DS-11), you must visit a passport acceptance facility (post office, courthouse, or library) for the in-person signature step — but you do not need to go to a regional passport agency yourself. We handle the agency submission.",
  },
  {
    question: "Why can't I just walk into a passport agency?",
    answer: "Regional passport agencies in the U.S. are strictly appointment-only. In 2026, appointments typically book 2–4 weeks in advance due to record passport demand. There is no walk-in service at any U.S. regional passport agency. This is precisely why registered couriers like Fast Passport Center exist — our reserved daily submission slots bypass the public appointment system entirely.",
  },
  {
    question: "What's the difference between urgent and emergency passport service?",
    answer: "Emergency service refers to situations where travel is within 72 hours and same-day or next-day processing is needed. Urgent service covers the broader 3–14 day window where you need your passport much faster than the standard government timeline but have slightly more breathing room. Fast Passport Center handles both — the right tier depends on your specific travel date.",
  },
  {
    question: "I already have an application in process — can you help speed it up?",
    answer: "Possibly. Call us at 877-253-0084 and tell us when you submitted, what type of passport you applied for, and your travel date. In some situations we can help escalate or, if necessary, help you withdraw and resubmit through our courier service on an urgent basis. We'll give you an honest assessment of what's possible.",
  },
  {
    question: "What if I live far from the nearest passport agency?",
    answer: "That's exactly who we exist for. Cities like Tampa, Charlotte, Nashville, Louisville, Sacramento, Kansas City, Bozeman, and Jackson Hole don't have a nearby regional passport agency — the nearest one may be 4–8 hours away. Our courier handles the agency trip on your behalf. You mail us your documents; we do everything else.",
  },
  {
    question: "How much does urgent passport service cost?",
    answer: "Our service fees range from $195 for 9-business-day processing to $345 for 3-business-day processing. Government fees are separate and required regardless of which service you use. We are typically 20–80% less expensive than retail counters like FedEx Office or Staples for the same urgent service.",
  },
  {
    question: "What documents do I need for an urgent passport?",
    answer: "It depends on your situation. For urgent renewal: current passport, Form DS-82, passport photo, and proof of travel. For urgent new passport: birth certificate, government ID, Form DS-11 (signed at acceptance facility), passport photo, and proof of travel. For urgent lost/stolen: DS-64, DS-11, birth certificate, government ID, passport photo, and proof of travel. We provide a personalized document checklist when you start your order.",
  },
  {
    question: "Is Fast Passport Center a legitimate registered passport courier?",
    answer: "Yes. Fast Passport Center has been registered with the U.S. Department of State as a passport courier for over 20 years and appears on the official State Dept. registry at travel.state.gov. We are A+ rated by the Better Business Bureau. Always verify any passport expediting service on the official government registry before using them.",
  },
  {
    question: "What if I'm traveling internationally and my destination requires 6 months passport validity?",
    answer: "Many countries require your passport to be valid for at least 6 months beyond your return date. If your passport expires within 6 months of your trip, you need to renew urgently even if it hasn't technically expired yet. Fast Passport Center handles exactly this situation every day — start your urgent renewal order now.",
  },
];

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-14 md:py-20 bg-white">
      <div className="max-w-3xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-4xl font-bold text-[#2D2D2D] mb-4">
            Urgent Passport — Frequently Asked Questions
          </h2>
        </div>

        <div className="space-y-3">
          {faqs.map((faq, i) => (
            <div key={i} className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => toggleFAQ(i)}
                className="w-full flex items-center justify-between p-5 text-left bg-white hover:bg-gray-50 transition-colors cursor-pointer"
              >
                <span className="text-sm md:text-base font-semibold text-[#2D2D2D] pr-4">{faq.question}</span>
                <div className={`flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full transition-colors ${openIndex === i ? "bg-amber-500 text-white" : "bg-gray-100 text-gray-500"}`}>
                  <i className={`text-lg ${openIndex === i ? "ri-subtract-line" : "ri-add-line"}`}></i>
                </div>
              </button>
              {openIndex === i && (
                <div className="px-5 pb-5">
                  <p className="text-gray-600 text-sm leading-relaxed">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-10 text-center">
          <a
            href="tel:8772530084"
            className="whitespace-nowrap inline-flex items-center gap-2 text-amber-700 hover:text-amber-800 font-semibold transition-colors cursor-pointer"
          >
            <i className="ri-phone-fill"></i>
            Still have questions? Call 877-253-0084
          </a>
        </div>
      </div>
    </section>
  );
}

export { faqs };